import React, { useRef, useEffect, useCallback } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "../../lib/maplibre-worker";
import type { MapFeatureCollection, HeatmapFeatureCollection, MapFeatureProperties } from "../../api/map";
import { cellToRing, decodeCellArray, type ExploredCell } from "@atlas/shared";
import type { FilterType } from "./FilterBar";
import { MAP_COLORS } from "./map-colors";

type FeatureCollection = { type: "FeatureCollection"; features: Feature[] };
type Feature = { type: "Feature"; geometry: { type: string; coordinates: unknown }; properties: Record<string, unknown> };
type PointGeom = { type: "Point"; coordinates: [number, number] };

// OpenFreeMap dark style — no API key required
const MAP_STYLE = "https://tiles.openfreemap.org/styles/dark";

// Default view frames the whole of the UK (SW to NE corners) rather than a
// single region. Used for the initial fit and the reset control.
const UK_BOUNDS: [[number, number], [number, number]] = [
  [-8.65, 49.9],
  [1.9, 60.9],
];

// Design token hex values (CSS vars can't be used in MapLibre paint) — see map-colors.ts
const { gold: GOLD, sky: SKY, grey: GREY, explored: EXPLORED, textOnGold: GOLD_TEXT, textOnGrey: GREY_TEXT } = MAP_COLORS;

// Explored-cells (fog of war, spec 13): z15 slippy tiles → one polygon each.
// Built client-side from the flat [x,y,…] wire array; no per-cell properties.
function cellsToFeatureCollection(cells: number[] | null | undefined): FeatureCollection {
  if (!cells || cells.length < 2) return EMPTY_FC as FeatureCollection;
  const decoded: ExploredCell[] = decodeCellArray(cells);
  return {
    type: "FeatureCollection",
    features: decoded.map((c) => ({
      type: "Feature" as const,
      properties: {},
      geometry: { type: "Polygon" as const, coordinates: [cellToRing(c)] },
    })),
  };
}

// ─── Equipped map theme (STO-4) ───────────────────────────────────────────────
// The equipped map_theme item's metadata, sourced from GET /profile/stats
// equipped.mapTheme — no new endpoint. heatRamp recolours the activity trace
// layer as a 5-stop gradient keyed by each trace's relative recency within the
// current viewport; traceColor is a flat override when no ramp is set.
export interface AtlasMapTheme {
  heatRamp?: string[] | null;
  traceColor?: string | null;
}

// Equipped trace_style item's metadata — restyles the activity trace STROKE
// (dash pattern / glow) independently of the map theme's colours.
export interface AtlasTraceStyle {
  dashArray?: number[] | null;
  glow?: boolean | null;
}

// Annotates each heatmap (GPS trace) feature with `atlasHeatT` — its recency
// (0 = oldest, 1 = newest) relative to the other traces currently loaded.
// Cheap and harmless to compute even when no theme is equipped.
function withHeatRecency(fc: FeatureCollection): FeatureCollection {
  const times = fc.features
    .map((f) => {
      const started = f.properties?.["startedAt"];
      return typeof started === "string" ? new Date(started).getTime() : null;
    })
    .filter((t): t is number => t != null && !Number.isNaN(t));

  if (times.length === 0) return fc;

  const min = Math.min(...times);
  const max = Math.max(...times);
  const span = max - min || 1;

  return {
    ...fc,
    features: fc.features.map((f) => {
      const started = f.properties?.["startedAt"];
      const t = typeof started === "string" ? new Date(started).getTime() : min;
      return { ...f, properties: { ...f.properties, atlasHeatT: (t - min) / span } };
    }),
  };
}

function applyHeatmapTheme(
  map: maplibregl.Map,
  theme: AtlasMapTheme | null | undefined,
  traceStyle?: AtlasTraceStyle | null,
) {
  if (!map.getLayer("activity-heatmap")) return;
  const ramp = theme?.heatRamp;
  const lineColor: maplibregl.ExpressionSpecification | string =
    ramp && ramp.length === 5
      ? ([
          "interpolate",
          ["linear"],
          ["coalesce", ["get", "atlasHeatT"], 1],
          0, ramp[0],
          0.25, ramp[1],
          0.5, ramp[2],
          0.75, ramp[3],
          1, ramp[4],
        ] as unknown as maplibregl.ExpressionSpecification)
      : (theme?.traceColor ?? GOLD);
  map.setPaintProperty("activity-heatmap", "line-color", lineColor);

  // Trace style: dash pattern on the main stroke, glow via the underlay
  // (always present, opacity-toggled — cheaper than adding/removing layers).
  const dash = traceStyle?.dashArray;
  map.setPaintProperty(
    "activity-heatmap",
    "line-dasharray",
    dash && dash.length >= 2 && dash.length % 2 === 0 ? dash : [1, 0],
  );
  if (map.getLayer("activity-heatmap-glow")) {
    map.setPaintProperty("activity-heatmap-glow", "line-color", lineColor);
    map.setPaintProperty("activity-heatmap-glow", "line-opacity", traceStyle?.glow ? 0.35 : 0);
  }
}

// Re-registers the "collected" marker images (peaks + every landmark category)
// and the landmark hint-dot colour with the equipped pin colour. null restores
// the default gold.
function applyCollectedPinColor(map: maplibregl.Map, color: string | null | undefined) {
  const bg = color ?? GOLD;
  const replaceImage = (id: string, img: ImageData) => {
    if (map.hasImage(id)) map.removeImage(id);
    map.addImage(id, img, { pixelRatio: 2 });
  };
  replaceImage("peak-collected", createMarkerImage(bg, PEAK_MARKER_PX, PEAK_GLYPH));
  replaceImage("peak-collected-trig", createMarkerImage(bg, PEAK_MARKER_PX, PEAK_GLYPH, { trigBadge: true }));
  replaceImage("peak-collected-trig-cam", createMarkerImage(bg, PEAK_MARKER_PX, PEAK_GLYPH, { trigBadge: true, cameraBadge: true }));
  for (const [category, glyph] of Object.entries(LANDMARK_GLYPHS)) {
    replaceImage(`lm-${category}-c`, createMarkerImage(bg, LANDMARK_MARKER_PX, glyph));
    replaceImage(`lm-${category}-c-cam`, createMarkerImage(bg, LANDMARK_MARKER_PX, glyph, { cameraBadge: true }));
  }
  if (map.getLayer("landmarks-dot")) {
    map.setPaintProperty("landmarks-dot", "circle-color", [
      "match",
      ["get", "status"],
      "collected",
      bg,
      GREY,
    ] as unknown as maplibregl.ExpressionSpecification);
  }
}

// ─── Marker images ────────────────────────────────────────────────────────────
// Circular badge + glyph, rendered on canvas at 2× and registered with
// pixelRatio 2 so icons stay crisp on retina displays. Peaks keep the filled
// lucide Mountain silhouette; each landmark category gets its own glyph —
// lucide path data where an icon fits, hand-drawn 24×24 paths where none
// exists (bridge arch, stone-circle dot ring, hut). Keep visually aligned
// with the React icon set in src/lib/landmark-icons.tsx.

export interface MarkerGlyph {
  /** SVG path data in a 24×24 viewbox. */
  paths?: string[];
  /** Fill paths instead of stroking them (peak silhouette). */
  fillPaths?: boolean;
  /** [cx, cy, r] circles in the same viewbox. */
  circles?: Array<[number, number, number]>;
  /** Fill circles instead of stroking them (stone-circle dots). */
  fillCircles?: boolean;
}

export const PEAK_GLYPH: MarkerGlyph = {
  paths: ["M8 3 L12 12 L17 7 L22 22 L2 22 Z"],
  fillPaths: true,
};

export const LANDMARK_GLYPHS: Record<string, MarkerGlyph> = {
  // Lucide Triangle — a pillar you sight from.
  trig_point: {
    paths: ["M13.73 4a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 20h16a2 2 0 0 0 1.73-3Z"],
  },
  // Lucide Droplet.
  waterfall: {
    paths: [
      "M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z",
    ],
  },
  // Lucide Eye.
  viewpoint: {
    paths: ["M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"],
    circles: [[12, 12, 3]],
  },
  // Lucide House.
  bothy: {
    paths: [
      "M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
      "M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8",
    ],
  },
  // Lucide Tent.
  shelter: {
    paths: ["M3.5 21 14 3", "M20.5 21 10 3", "M15.5 21 12 15l-3.5 6", "M2 21h20"],
  },
  // Hand-drawn A-frame cabin: full-height pitched roof, floor, door.
  mountain_hut: {
    paths: ["M3.5 21 12 4l8.5 17", "M2 21h20", "M9.5 21v-5.5h5V21"],
  },
  // Hand-drawn ring of standing stones.
  stone_circle: {
    circles: [
      [12, 4.5, 1.9],
      [18.5, 8.25, 1.9],
      [18.5, 15.75, 1.9],
      [12, 19.5, 1.9],
      [5.5, 15.75, 1.9],
      [5.5, 8.25, 1.9],
    ],
    fillCircles: true,
  },
  // Hand-drawn bridge: deck, piers, arch.
  bridge: {
    paths: ["M2 8h20", "M5 8v13", "M19 8v13", "M5 21a7 7 0 0 1 14 0"],
  },
  // Hand-drawn cave mouth: ground line, arched entrance (mirrored in
  // lib/landmark-icons.tsx CaveIcon).
  cave: {
    paths: ["M2 21h20", "M5 21v-8.5a7 7 0 0 1 14 0V21"],
  },
};

const GLYPH_INK = "rgba(12, 18, 28, 0.85)";

export function createMarkerImage(
  bgColor: string,
  cssSize: number,
  glyph: MarkerGlyph,
  opts?: { trigBadge?: boolean; cameraBadge?: boolean; scale?: number },
): ImageData {
  // `scale` redraws the same marker on a bigger bitmap, for use outside the
  // map where it is shown larger than the map draws it (landing page).
  const k = opts?.scale ?? 1;
  const size = cssSize * 2 * k; // rendered @2x, registered with pixelRatio 2
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const r = size / 2;

  ctx.beginPath();
  ctx.arc(r, r, r - 3 * k, 0, Math.PI * 2);
  ctx.fillStyle = bgColor;
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.22)";
  ctx.lineWidth = 3 * k;
  ctx.stroke();

  // Scale the 24×24 glyph viewbox into the badge with even padding.
  const pad = size * 0.22;
  const sc = (size - pad * 2) / 24;
  ctx.translate(pad, pad);
  ctx.scale(sc, sc);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const d of glyph.paths ?? []) {
    const path = new Path2D(d);
    if (glyph.fillPaths) {
      ctx.fillStyle = GLYPH_INK;
      ctx.fill(path);
    } else {
      ctx.strokeStyle = GLYPH_INK;
      ctx.lineWidth = 2.4;
      ctx.stroke(path);
    }
  }
  for (const [cx, cy, cr] of glyph.circles ?? []) {
    ctx.beginPath();
    ctx.arc(cx, cy, cr, 0, Math.PI * 2);
    if (glyph.fillCircles) {
      ctx.fillStyle = GLYPH_INK;
      ctx.fill();
    } else {
      ctx.strokeStyle = GLYPH_INK;
      ctx.lineWidth = 2.4;
      ctx.stroke();
    }
  }

  // Corner badge marking a summit with a trig pillar — the pillar's own
  // marker is collapsed into this one whenever peaks are on the canvas.
  if (opts?.trigBadge) {
    ctx.resetTransform();
    const bc = size * 0.76;
    const br = size * 0.19;
    ctx.beginPath();
    ctx.arc(bc, bc, br, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.94)";
    ctx.fill();
    ctx.strokeStyle = "rgba(12, 18, 28, 0.3)";
    ctx.lineWidth = 1.5 * k;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(bc, bc - br * 0.52);
    ctx.lineTo(bc + br * 0.55, bc + br * 0.42);
    ctx.lineTo(bc - br * 0.55, bc + br * 0.42);
    ctx.closePath();
    ctx.fillStyle = GLYPH_INK;
    ctx.fill();
  }

  // Top-right camera badge — the user has a photo check-in at this landmark.
  // (Top corner so it can coexist with the bottom-right trig badge.)
  if (opts?.cameraBadge) {
    ctx.resetTransform();
    const bx = size * 0.76;
    const by = size * 0.24;
    const br = size * 0.19;
    ctx.beginPath();
    ctx.arc(bx, by, br, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.94)";
    ctx.fill();
    ctx.strokeStyle = "rgba(12, 18, 28, 0.3)";
    ctx.lineWidth = 1.5 * k;
    ctx.stroke();
    // Camera: viewfinder bump + body, lens knocked out in the badge white.
    const bw = br * 1.24;
    const bh = br * 0.92;
    ctx.fillStyle = GLYPH_INK;
    ctx.beginPath();
    ctx.roundRect(bx - br * 0.3, by - bh / 2 - br * 0.12, br * 0.6, br * 0.3, br * 0.08);
    ctx.fill();
    ctx.beginPath();
    ctx.roundRect(bx - bw / 2, by - bh / 2 + br * 0.04, bw, bh, br * 0.18);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(bx, by + br * 0.08, br * 0.26, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.94)";
    ctx.fill();
  }

  return ctx.getImageData(0, 0, size, size);
}

// ─── Marker sizing ────────────────────────────────────────────────────────────
// Peaks are the headline collectible: bigger badges, visible from further out
// (they also cluster below z9). Landmarks are deliberately quieter: hint dots
// from z8, full category icons only past LANDMARK_ICON_MINZOOM.

export const PEAK_MARKER_PX = 28;
export const LANDMARK_MARKER_PX = 20;
const LANDMARK_DOT_MINZOOM = 8;
const LANDMARK_ICON_MINZOOM = 10.5;
// Trig pillars are ~6.4k of the ~9k landmarks (OS archive, ATL-85). On the
// Landmarks tab, where other categories show at any zoom, pillars only join
// in from here so they don't blanket the country view.
const TRIG_LANDMARKS_TAB_MINZOOM = 8;

// Zoom ramp × selected-boost. MapLibre only allows ["zoom"] inside a
// TOP-LEVEL interpolate/step, so the selection multiplier lives in each
// stop's output rather than as an outer ["*", …].
const SELECTED_BOOST = 1.35;

function zoomSizeExpr(
  stops: Array<[number, number]>,
  selectedId: string | null,
): maplibregl.ExpressionSpecification {
  const selected = ["==", ["get", "id"], selectedId ?? ""];
  return [
    "interpolate",
    ["linear"],
    ["zoom"],
    ...stops.flatMap(([zoom, size]) => [
      zoom,
      ["case", selected, size * SELECTED_BOOST, size],
    ]),
  ] as unknown as maplibregl.ExpressionSpecification;
}

function peakIconSize(
  selectedId: string | null,
  opts?: { unclustered?: boolean },
): maplibregl.ExpressionSpecification {
  // Without clustering every peak renders individually at any zoom, so shrink
  // badges further out — a whole-country fitBounds otherwise draws full-size
  // markers on top of each other.
  const stops: Array<[number, number]> = opts?.unclustered
    ? [[4, 0.55], [7, 0.8], [11, 1], [15, 1.2]]
    : [[7, 0.8], [11, 1], [15, 1.2]];
  return zoomSizeExpr(stops, selectedId);
}

function landmarkIconSize(selectedId: string | null): maplibregl.ExpressionSpecification {
  return zoomSizeExpr([[LANDMARK_ICON_MINZOOM, 0.8], [14, 1.1]], selectedId);
}

// peak-<status>[-trig][-cam]: summits with a trig pillar get the badged
// variant; a camera badge joins it when the user has a check-in at that
// pillar (hasCheckin implies hasTrigPillar server-side, but the guard keeps a
// stray flag from requesting a -cam image that doesn't exist).
const PEAK_ICON_IMAGE = [
  "concat",
  ["match", ["get", "status"], "collected", "peak-collected", "peak-not-collected"],
  ["case", ["==", ["get", "hasTrigPillar"], true], "-trig", ""],
  [
    "case",
    ["all", ["==", ["get", "hasTrigPillar"], true], ["==", ["get", "hasCheckin"], true]],
    "-cam",
    "",
  ],
] as unknown as maplibregl.ExpressionSpecification;

// lm-<category>-c / lm-<category>-n, falling back to the flag-less trig glyph
// for any category the client doesn't know yet. Collected landmarks with a
// photo check-in get the camera-badged -c-cam variant (check-ins imply
// collected, so no -n-cam images exist — the status guard keeps a stray
// hasCheckin from requesting one).
const LANDMARK_ICON_IMAGE = [
  "concat",
  "lm-",
  ["coalesce", ["get", "category"], "trig_point"],
  ["case", ["==", ["get", "status"], "collected"], "-c", "-n"],
  [
    "case",
    ["all", ["==", ["get", "status"], "collected"], ["==", ["get", "hasCheckin"], true]],
    "-cam",
    "",
  ],
] as unknown as maplibregl.ExpressionSpecification;

export interface AtlasMapControls {
  zoom: (delta: number) => void;
  reset: () => void;
  flyTo: (center: [number, number], zoom?: number) => void;
  /** Request the browser's location (prompting if needed), drop the dot and fly to it. */
  locate: () => void;
}

interface AtlasMapProps {
  features: MapFeatureCollection | null;
  heatmap: HeatmapFeatureCollection | null;
  selectedId: string | null;
  filter: FilterType;
  /** Landmark categories to show (applies on any filter that renders landmarks). */
  landmarkCategories: string[];
  onFeatureSelect: (f: MapFeatureProperties) => void;
  onBoundsChange: (bbox: [number, number, number, number]) => void;
  controlsRef?: React.RefObject<AtlasMapControls | null>;
  /** Fit the camera to this [w,s,e,n] bbox once (and whenever it changes). */
  fitBounds?: [number, number, number, number] | null;
  /** Polygon(s) drawn as a Sky fill + dashed outline (region deep links, region collections). */
  regionOverlay?: FeatureCollection | null;
  /**
   * Explored cells (spec 13 fog of war): flat [x0,y0,x1,y1,…] z15 tile array
   * from GET /map/explored. Drawn as a low-opacity ember fill under every
   * other layer; null/empty draws nothing. Visibility is the caller's toggle.
   */
  exploredCells?: number[] | null;
  /** Equipped map theme's colours (STO-4); falls back to the default gold trace when absent. */
  mapTheme?: AtlasMapTheme | null;
  /** Equipped trace_style's stroke (dash/glow); default is a solid line. */
  traceStyle?: AtlasTraceStyle | null;
  /** Equipped pin's colour for collected markers; default is gold. */
  collectedPinColor?: string | null;
  /**
   * Draw the GPS-trace layer ABOVE trail lines (default is below). Trail
   * detail pages use this so the "walked" overlay reads on top of the
   * section geometry instead of bleeding through it. Fixed at mount.
   */
  traceAboveTrails?: boolean;
  /**
   * Cluster peaks into count bubbles below z9 (default). Fixed at mount —
   * the source is configured once in the load handler (Showcase sets false).
   */
  cluster?: boolean;
  /**
   * Show a current-location dot when geolocation permission is already
   * granted (never prompts by itself; the locate() control prompts). Fixed
   * at mount.
   */
  showUserLocation?: boolean;
  /**
   * Pin the camera to this [w,s,e,n] area: no panning or scroll zoom, and
   * zoom limited to a couple of levels in from the fitted view. Selecting a
   * pin or using the zoom controls still works. For showpiece maps embedded
   * in a scrolling page (the landing page). Fixed at mount.
   */
  lockedBounds?: [number, number, number, number] | null;
}

/** How far a locked map may zoom in from its fitted view. */
const LOCKED_ZOOM_RANGE = 2.2;

const EMPTY_FC: FeatureCollection = { type: "FeatureCollection", features: [] };

/** Peaks the current filter puts in the peaks source (clustering only sees these). */
function visiblePeaksFor(
  allPeaks: MapFeatureCollection["features"],
  filter: FilterType,
): MapFeatureCollection["features"] {
  return filter === "completed"
    ? allPeaks.filter((f) => f.properties.status === "collected")
    : filter === "gaps"
      ? allPeaks.filter((f) => f.properties.status === "not_collected")
      : allPeaks;
}

/**
 * Tag each peak-linked landmark with whether its peak is in the peaks source,
 * so the trig dedupe filter only hides a pillar that has a peak marker to
 * stand in for it.
 */
function markPeakShown(
  landmarks: MapFeatureCollection["features"],
  peaks: MapFeatureCollection["features"],
): MapFeatureCollection["features"] {
  const shown = new Set(peaks.map((f) => f.properties.id));
  return landmarks.map((f) => {
    const peakId = (f.properties as { peakId?: string }).peakId;
    if (!peakId || !shown.has(peakId)) return f;
    return { ...f, properties: { ...f.properties, peakShown: true } };
  });
}

function applyFilter(map: maplibregl.Map, filter: FilterType, landmarkCategories: string[]) {
  const gapExpr: maplibregl.FilterSpecification = ["==", ["get", "status"], "not_collected"];
  const collectedExpr: maplibregl.FilterSpecification = ["==", ["get", "status"], "collected"];

  // All known categories selected → no category expression, so landmarks the
  // client doesn't know yet still render (with the fallback glyph).
  const allCatsOn = landmarkCategories.length >= Object.keys(LANDMARK_GLYPHS).length;
  const catExpr: maplibregl.FilterSpecification = [
    "in",
    ["get", "category"],
    ["literal", landmarkCategories],
  ];

  const showPeaks = filter === "all" || filter === "peaks" || filter === "gaps" || filter === "completed";
  const showTrails = filter === "all" || filter === "trails" || filter === "gaps" || filter === "completed";
  const showLandmarks =
    (filter === "all" || filter === "landmarks" || filter === "gaps" || filter === "completed") &&
    landmarkCategories.length > 0;

  const setVis = (id: string, vis: boolean) => {
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, "visibility", vis ? "visible" : "none");
    }
  };

  setVis("peaks-symbol", showPeaks);
  setVis("peaks-cluster", showPeaks);
  setVis("peaks-cluster-count", showPeaks);
  setVis("trails-line", showTrails);
  setVis("landmarks-symbol", showLandmarks);

  // Landmarks are normally staged in quietly (hint dots, icons only past
  // z10.5) so they don't compete with peaks. On the dedicated Landmarks tab
  // they ARE the content: show full icons at any zoom (collision placement
  // still declutters) and drop the dots.
  const landmarksOnly = filter === "landmarks";
  if (map.getLayer("landmarks-symbol")) {
    map.setLayerZoomRange("landmarks-symbol", landmarksOnly ? 0 : LANDMARK_ICON_MINZOOM, 24);
  }
  setVis("landmarks-dot", showLandmarks && !landmarksOnly);

  // Peak-linked trig pillars collapse into their peak's badged marker
  // whenever that peak is on the canvas; on the Landmarks tab (peaks hidden)
  // they stand on their own. `peakShown` is set at source level
  // (markPeakShown), so a pillar whose peak is filtered out or absent (a
  // showcase with the trig but not the summit) still draws.
  const trigDedupeExpr: maplibregl.FilterSpecification = ["!", ["==", ["get", "peakShown"], true]];

  const notTrigExpr: maplibregl.FilterSpecification = ["!=", ["get", "category"], "trig_point"];

  const setLandmarkFilter = (statusExpr: maplibregl.FilterSpecification | null) => {
    const exprs = [
      ...(statusExpr ? [statusExpr] : []),
      ...(allCatsOn ? [] : [catExpr]),
      ...(landmarksOnly ? [] : [trigDedupeExpr]),
    ];
    const combine = (list: maplibregl.FilterSpecification[]): maplibregl.FilterSpecification | null =>
      list.length === 0 ? null : list.length === 1 ? list[0]! : (["all", ...list] as maplibregl.FilterSpecification);
    let expr = combine(exprs);
    // Landmarks tab shows icons at any zoom, but trig pillars (the bulk of
    // the layer) wait until TRIG_LANDMARKS_TAB_MINZOOM. A filter may only
    // read ["zoom"] as the input of a top-level "step", hence the wrapper.
    if (landmarksOnly) {
      expr = [
        "step",
        ["zoom"],
        combine([...exprs, notTrigExpr]) ?? true,
        TRIG_LANDMARKS_TAB_MINZOOM,
        expr ?? true,
      ] as unknown as maplibregl.FilterSpecification;
    }
    for (const id of ["landmarks-symbol", "landmarks-dot"]) {
      if (map.getLayer(id)) map.setFilter(id, expr);
    }
  };

  if (filter === "gaps") {
    if (map.getLayer("peaks-symbol")) map.setFilter("peaks-symbol", ["all", ["!", ["has", "point_count"]], gapExpr]);
    if (map.getLayer("trails-line")) map.setFilter("trails-line", gapExpr);
    setLandmarkFilter(gapExpr);
  } else if (filter === "completed") {
    if (map.getLayer("peaks-symbol")) map.setFilter("peaks-symbol", ["all", ["!", ["has", "point_count"]], collectedExpr]);
    if (map.getLayer("trails-line")) map.setFilter("trails-line", collectedExpr);
    setLandmarkFilter(collectedExpr);
  } else {
    if (map.getLayer("peaks-symbol")) map.setFilter("peaks-symbol", ["!", ["has", "point_count"]]);
    if (map.getLayer("trails-line")) map.setFilter("trails-line", null);
    setLandmarkFilter(null);
  }
}

export function AtlasMap({
  features,
  heatmap,
  selectedId,
  filter,
  landmarkCategories,
  onFeatureSelect,
  onBoundsChange,
  controlsRef,
  fitBounds,
  regionOverlay,
  exploredCells,
  mapTheme,
  traceStyle,
  collectedPinColor,
  cluster = true,
  traceAboveTrails = false,
  showUserLocation = false,
  lockedBounds = null,
}: AtlasMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  // Deliberately never updated — clustering is a source-creation option.
  const clusterRef = useRef(cluster);
  const showUserLocationRef = useRef(showUserLocation);
  const lockedBoundsRef = useRef(lockedBounds);
  const geoWatchRef = useRef<number | null>(null);
  const lastLocationRef = useRef<[number, number] | null>(null);
  const pendingLocateRef = useRef(false);
  // Fixed at mount, like cluster — layer order is set once at layer creation.
  const traceAboveTrailsRef = useRef(traceAboveTrails);
  const sourcesReadyRef = useRef(false);
  const featuresRef = useRef<MapFeatureCollection | null>(null);
  const heatmapRef = useRef<HeatmapFeatureCollection | null>(null);
  const regionOverlayRef = useRef<FeatureCollection | null | undefined>(null);
  const exploredCellsRef = useRef<number[] | null | undefined>(null);
  const mapThemeRef = useRef<AtlasMapTheme | null | undefined>(null);
  const traceStyleRef = useRef<AtlasTraceStyle | null | undefined>(null);
  const pinColorRef = useRef<string | null | undefined>(null);
  const filterRef = useRef<FilterType>(filter);
  const landmarkCatsRef = useRef<string[]>(landmarkCategories);

  // Keep refs in sync so the load handler can read the latest data
  featuresRef.current = features;
  heatmapRef.current = heatmap;
  regionOverlayRef.current = regionOverlay;
  exploredCellsRef.current = exploredCells;
  mapThemeRef.current = mapTheme;
  traceStyleRef.current = traceStyle;
  pinColorRef.current = collectedPinColor;
  filterRef.current = filter;
  landmarkCatsRef.current = landmarkCategories;

  const emitBounds = useCallback(
    (map: maplibregl.Map) => {
      const b = map.getBounds();
      onBoundsChange([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    },
    [onBoundsChange]
  );

  // Initialise map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const locked = lockedBoundsRef.current;
    const lockedBox: [[number, number], [number, number]] | null = locked
      ? [[locked[0], locked[1]], [locked[2], locked[3]]]
      : null;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      bounds: lockedBox ?? UK_BOUNDS,
      fitBoundsOptions: { padding: lockedBox ? 0 : 24 },
      attributionControl: false,
      ...(lockedBox
        ? {
            dragPan: false,
            scrollZoom: false,
            dragRotate: false,
            keyboard: false,
            boxZoom: false,
            touchPitch: false,
            pitchWithRotate: false,
          }
        : {}),
    });
    if (lockedBox) map.touchZoomRotate.disableRotation();

    mapRef.current = map;

    map.on("load", () => {
      const clusterEnabled = clusterRef.current;

      // Locked view: the fitted camera is the widest the map goes. maxBounds
      // is the fitted viewport, not the requested box - a box narrower than
      // the viewport makes MapLibre silently re-centre the camera.
      if (lockedBox) {
        map.setMinZoom(map.getZoom());
        map.setMaxZoom(map.getZoom() + LOCKED_ZOOM_RANGE);
        map.setMaxBounds(map.getBounds());
      }

      // Sources
      map.addSource("peaks", {
        type: "geojson",
        data: EMPTY_FC as maplibregl.GeoJSONSourceSpecification["data"],
        ...(clusterEnabled
          ? {
              cluster: true,
              clusterMaxZoom: 9,
              clusterRadius: 40,
              clusterProperties: {
                collected_count: ["+", ["case", ["==", ["get", "status"], "collected"], 1, 0]],
              },
            }
          : {}),
      });
      map.addSource("trails", { type: "geojson", data: EMPTY_FC as maplibregl.GeoJSONSourceSpecification["data"] });
      map.addSource("landmarks", { type: "geojson", data: EMPTY_FC as maplibregl.GeoJSONSourceSpecification["data"] });
      map.addSource("heatmap", { type: "geojson", data: EMPTY_FC as maplibregl.GeoJSONSourceSpecification["data"] });
      map.addSource("region-overlay", { type: "geojson", data: EMPTY_FC as maplibregl.GeoJSONSourceSpecification["data"] });
      map.addSource("explored-cells", {
        type: "geojson",
        data: EMPTY_FC as maplibregl.GeoJSONSourceSpecification["data"],
        // Cells are axis-aligned boxes; no simplification needed and it keeps
        // shared edges exact so adjacent cells never show a seam.
        tolerance: 0,
      });

      // Explored cells (spec 13 fog of war) — the lowest of our layers, under
      // the region overlay, trails, traces and every marker. Reveal, not
      // darken: only explored cells are painted. Fades out above z12 where
      // ~720 m blocks stop reading as "explored ground" and start reading as
      // pixels, and below z6 where it is noise. Antialias off so the shared
      // edges of neighbouring cells don't hairline.
      map.addLayer({
        id: "explored-cells-fill",
        type: "fill",
        source: "explored-cells",
        paint: {
          "fill-color": EXPLORED,
          "fill-antialias": false,
          "fill-opacity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6, 0,
            7, 0.2,
            11.5, 0.2,
            12.5, 0,
          ],
        },
      });

      // Region polygon overlay (deep links + region collections) — drawn below points/lines
      map.addLayer({
        id: "region-overlay-fill",
        type: "fill",
        source: "region-overlay",
        paint: { "fill-color": SKY, "fill-opacity": 0.12 },
      });
      map.addLayer({
        id: "region-overlay-outline",
        type: "line",
        source: "region-overlay",
        paint: {
          "line-color": SKY,
          "line-width": 2,
          "line-dasharray": [3, 2],
        },
      });

      // Glow underlay for the trace (STO trace_style) — always present,
      // invisible until a glow style is equipped.
      map.addLayer({
        id: "activity-heatmap-glow",
        type: "line",
        source: "heatmap",
        paint: {
          "line-color": GOLD,
          "line-opacity": 0,
          "line-width": 11,
          "line-blur": 6,
        },
      });

      // Activity heatmap layer — user GPS traces
      map.addLayer({
        id: "activity-heatmap",
        type: "line",
        source: "heatmap",
        paint: {
          "line-color": GOLD,
          "line-opacity": 0.65,
          "line-width": 4,
        },
      });

      // Trail line layer
      map.addLayer({
        id: "trails-line",
        type: "line",
        source: "trails",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": [
            "match",
            ["get", "status"],
            "collected", GOLD,
            "in_progress", SKY,
            GREY,
          ],
          "line-width": 3,
          "line-opacity": 0.85,
          "line-dasharray": ["case", ["==", ["get", "status"], "not_collected"], ["literal", [4, 3]], ["literal", [1, 0]]],
        },
      });

      if (clusterEnabled) {
        // Cluster circles
        map.addLayer({
          id: "peaks-cluster",
          type: "circle",
          source: "peaks",
          filter: ["has", "point_count"],
          paint: {
            "circle-color": ["case", [">", ["get", "collected_count"], 0], GOLD, GREY],
            "circle-radius": ["step", ["get", "point_count"], 16, 10, 22, 30, 28],
            "circle-opacity": 0.85,
          },
        });

        // Cluster count labels
        map.addLayer({
          id: "peaks-cluster-count",
          type: "symbol",
          source: "peaks",
          filter: ["has", "point_count"],
          layout: {
            "text-field": "{point_count_abbreviated}",
            "text-font": ["Noto Sans Bold"],
            "text-size": 12,
          },
          paint: { "text-color": ["case", [">", ["get", "collected_count"], 0], GOLD_TEXT, GREY_TEXT] },
        });
      }

      // Register marker images: peaks (± trig-pillar badge) + one per
      // landmark category × status
      map.addImage("peak-collected",     createMarkerImage(GOLD, PEAK_MARKER_PX, PEAK_GLYPH), { pixelRatio: 2 });
      map.addImage("peak-not-collected", createMarkerImage(GREY, PEAK_MARKER_PX, PEAK_GLYPH), { pixelRatio: 2 });
      map.addImage("peak-collected-trig",     createMarkerImage(GOLD, PEAK_MARKER_PX, PEAK_GLYPH, { trigBadge: true }), { pixelRatio: 2 });
      map.addImage("peak-not-collected-trig", createMarkerImage(GREY, PEAK_MARKER_PX, PEAK_GLYPH, { trigBadge: true }), { pixelRatio: 2 });
      map.addImage("peak-collected-trig-cam",     createMarkerImage(GOLD, PEAK_MARKER_PX, PEAK_GLYPH, { trigBadge: true, cameraBadge: true }), { pixelRatio: 2 });
      map.addImage("peak-not-collected-trig-cam", createMarkerImage(GREY, PEAK_MARKER_PX, PEAK_GLYPH, { trigBadge: true, cameraBadge: true }), { pixelRatio: 2 });
      for (const [category, glyph] of Object.entries(LANDMARK_GLYPHS)) {
        map.addImage(`lm-${category}-c`, createMarkerImage(GOLD, LANDMARK_MARKER_PX, glyph), { pixelRatio: 2 });
        map.addImage(`lm-${category}-n`, createMarkerImage(GREY, LANDMARK_MARKER_PX, glyph), { pixelRatio: 2 });
        map.addImage(`lm-${category}-c-cam`, createMarkerImage(GOLD, LANDMARK_MARKER_PX, glyph, { cameraBadge: true }), { pixelRatio: 2 });
      }

      // Individual peak symbols
      map.addLayer({
        id: "peaks-symbol",
        type: "symbol",
        source: "peaks",
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-image": PEAK_ICON_IMAGE,
          "icon-size": peakIconSize(null, { unclustered: !clusterEnabled }),
          "icon-allow-overlap": true,
          "icon-anchor": "center",
        },
      });

      // Landmark hint dots — presence without clutter before icons kick in
      map.addLayer({
        id: "landmarks-dot",
        type: "circle",
        source: "landmarks",
        minzoom: LANDMARK_DOT_MINZOOM,
        maxzoom: LANDMARK_ICON_MINZOOM,
        paint: {
          "circle-color": ["match", ["get", "status"], "collected", GOLD, GREY],
          "circle-radius": ["interpolate", ["linear"], ["zoom"], LANDMARK_DOT_MINZOOM, 1.5, LANDMARK_ICON_MINZOOM, 3],
          "circle-opacity": ["interpolate", ["linear"], ["zoom"], LANDMARK_DOT_MINZOOM, 0.35, LANDMARK_DOT_MINZOOM + 1, 0.75],
        },
      });

      // Landmark category icons — zoomed-in view. Collision placement is left
      // on so dense areas declutter themselves and fill in as you zoom;
      // collected ones win placement over uncollected.
      map.addLayer({
        id: "landmarks-symbol",
        type: "symbol",
        source: "landmarks",
        minzoom: LANDMARK_ICON_MINZOOM,
        layout: {
          "icon-image": LANDMARK_ICON_IMAGE,
          "icon-size": landmarkIconSize(null),
          "icon-anchor": "center",
          "symbol-sort-key": ["case", ["==", ["get", "status"], "collected"], 0, 1],
        },
      });

      // Current-location dot, drawn above every feature layer. The source
      // stays empty until a fix arrives, so the layers cost nothing when
      // geolocation is off or denied.
      map.addSource("user-location", { type: "geojson", data: EMPTY_FC as maplibregl.GeoJSONSourceSpecification["data"] });
      map.addLayer({
        id: "user-location-halo",
        type: "circle",
        source: "user-location",
        paint: { "circle-radius": 16, "circle-color": SKY, "circle-opacity": 0.18, "circle-blur": 0.4 },
      });
      map.addLayer({
        id: "user-location-dot",
        type: "circle",
        source: "user-location",
        paint: {
          "circle-radius": 7,
          "circle-color": SKY,
          "circle-stroke-width": 2.5,
          "circle-stroke-color": "#FFFFFF",
        },
      });

      const updateUserLocation = (pos: GeolocationPosition) => {
        const coords: [number, number] = [pos.coords.longitude, pos.coords.latitude];
        lastLocationRef.current = coords;
        (map.getSource("user-location") as maplibregl.GeoJSONSource | undefined)?.setData({
          type: "FeatureCollection",
          features: [{ type: "Feature", geometry: { type: "Point", coordinates: coords }, properties: {} }],
        });
        if (pendingLocateRef.current) {
          pendingLocateRef.current = false;
          map.flyTo({ center: coords, zoom: Math.max(map.getZoom(), 13) });
        }
      };
      const startLocationWatch = () => {
        if (geoWatchRef.current != null || !("geolocation" in navigator)) return;
        geoWatchRef.current = navigator.geolocation.watchPosition(
          updateUserLocation,
          () => {
            // Denied or unavailable — clear the id so a later locate() retries
            // (and re-prompts if the user changes the permission).
            if (geoWatchRef.current != null) navigator.geolocation.clearWatch(geoWatchRef.current);
            geoWatchRef.current = null;
            pendingLocateRef.current = false;
          },
          { enableHighAccuracy: false, maximumAge: 30_000 }
        );
      };
      // Auto-show the dot only when permission is already granted — never
      // trigger the browser prompt just by opening the map.
      if (showUserLocationRef.current && "geolocation" in navigator) {
        navigator.permissions
          ?.query({ name: "geolocation" })
          .then((status) => {
            if (status.state === "granted") startLocationWatch();
          })
          .catch(() => {});
      }

      sourcesReadyRef.current = true;

      // moveLayer(id, beforeId) places id UNDER beforeId — pushing the trail
      // lines beneath the glow underlay puts the whole trace stack on top.
      // Collected sections then get a dedicated opaque gold layer above the
      // trace: a finished section must read as continuous gold, never mixed
      // with the walked-overlay colour.
      if (traceAboveTrailsRef.current && map.getLayer("trails-line") && map.getLayer("activity-heatmap-glow")) {
        map.moveLayer("trails-line", "activity-heatmap-glow");
        map.addLayer({
          id: "trails-line-collected-top",
          type: "line",
          source: "trails",
          filter: ["==", ["get", "status"], "collected"],
          layout: { "line-cap": "round", "line-join": "round" },
          paint: {
            "line-color": GOLD,
            "line-width": 4,
            "line-opacity": 1,
          },
        });
      }

      // Honour the active filter (may be a non-default initialFilter, or a
      // change made while the style was still loading)
      applyFilter(map, filterRef.current, landmarkCatsRef.current);

      // Apply any data that arrived from cache before this load event fired
      const pendingHeatmap = heatmapRef.current;
      if (pendingHeatmap) {
        (map.getSource("heatmap") as maplibregl.GeoJSONSource).setData(
          withHeatRecency(pendingHeatmap as FeatureCollection)
        );
      }
      applyHeatmapTheme(map, mapThemeRef.current, traceStyleRef.current);
      if (pinColorRef.current) applyCollectedPinColor(map, pinColorRef.current);
      const pendingExplored = exploredCellsRef.current;
      if (pendingExplored && pendingExplored.length) {
        (map.getSource("explored-cells") as maplibregl.GeoJSONSource).setData(
          cellsToFeatureCollection(pendingExplored) as maplibregl.GeoJSONSourceSpecification["data"]
        );
      }
      const pendingRegionOverlay = regionOverlayRef.current;
      if (pendingRegionOverlay) {
        (map.getSource("region-overlay") as maplibregl.GeoJSONSource).setData(pendingRegionOverlay);
      }
      const pendingFeatures = featuresRef.current;
      if (pendingFeatures) {
        const pf = pendingFeatures.features;
        const pendingPeaks = visiblePeaksFor(
          pf.filter((f) => f.properties.type === "peak"),
          filterRef.current,
        );
        (map.getSource("peaks") as maplibregl.GeoJSONSource).setData({
          type: "FeatureCollection",
          features: pendingPeaks as unknown as Feature[],
        });
        (map.getSource("trails") as maplibregl.GeoJSONSource).setData({
          type: "FeatureCollection",
          features: pf.filter((f) => f.properties.type === "trail") as unknown as Feature[],
        });
        (map.getSource("landmarks") as maplibregl.GeoJSONSource).setData({
          type: "FeatureCollection",
          features: markPeakShown(
            pf.filter((f) => f.properties.type === "landmark"),
            pendingPeaks,
          ) as unknown as Feature[],
        });
      }

      // Click handlers
      map.on("click", "peaks-symbol", (e) => {
        const f = e.features?.[0];
        if (f) onFeatureSelect(f.properties as MapFeatureProperties);
      });
      map.on("click", "landmarks-symbol", (e) => {
        const f = e.features?.[0];
        if (f) onFeatureSelect(f.properties as MapFeatureProperties);
      });
      map.on("click", "trails-line", (e) => {
        const f = e.features?.[0];
        if (f) onFeatureSelect(f.properties as MapFeatureProperties);
      });
      if (clusterEnabled) {
        map.on("click", "peaks-cluster", (e) => {
          const features = map.queryRenderedFeatures(e.point, { layers: ["peaks-cluster"] });
          const clusterId = features[0]?.properties?.["cluster_id"] as number | undefined;
          if (clusterId == null) return;
          const src = map.getSource("peaks") as maplibregl.GeoJSONSource;
          src.getClusterExpansionZoom(clusterId).then((zoom) => {
            const coords = (features[0]?.geometry as PointGeom)?.coordinates;
            if (coords) {
              map.easeTo({ center: [coords[0], coords[1]], zoom });
            }
          });
        });
      }

      // Cursor pointers
      for (const layer of [
        "peaks-symbol",
        "landmarks-symbol",
        "trails-line",
        ...(clusterEnabled ? ["peaks-cluster"] : []),
      ]) {
        map.on("mouseenter", layer, () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", layer, () => { map.getCanvas().style.cursor = ""; });
      }

      // Expose imperative controls
      if (controlsRef) {
        (controlsRef as React.MutableRefObject<AtlasMapControls | null>).current = {
          zoom: (delta) => map.setZoom(map.getZoom() + delta),
          reset: () => map.fitBounds(lockedBox ?? UK_BOUNDS, { padding: lockedBox ? 0 : 24 }),
          // Zoom and centre are clamped by maxZoom/maxBounds when locked.
          flyTo: (center, zoom) => map.flyTo({ center, zoom: zoom ?? Math.max(map.getZoom(), 13) }),
          locate: () => {
            if (!("geolocation" in navigator)) return;
            const last = lastLocationRef.current;
            if (last) {
              map.flyTo({ center: last, zoom: Math.max(map.getZoom(), 13) });
            } else {
              // Fly as soon as the watch delivers its first fix (the watch
              // also triggers the permission prompt if still needed).
              pendingLocateRef.current = true;
            }
            startLocationWatch();
          },
        };
      }

      // Bounds reporting
      map.on("moveend", () => emitBounds(map));
      map.on("zoomend", () => emitBounds(map));
      emitBounds(map);
    });

    return () => {
      if (geoWatchRef.current != null) {
        navigator.geolocation.clearWatch(geoWatchRef.current);
        geoWatchRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        sourcesReadyRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update sources when feature data or filter changes.
  // Peaks are pre-filtered at the source level so MapLibre clustering only
  // aggregates the features that should actually be visible.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sourcesReadyRef.current) return;

    const allPeaks = features?.features.filter((f) => f.properties.type === "peak") ?? [];
    const trailFeatures = features?.features.filter((f) => f.properties.type === "trail") ?? [];
    const landmarkFeatures = features?.features.filter((f) => f.properties.type === "landmark") ?? [];

    const peakFeatures = visiblePeaksFor(allPeaks, filter);

    (map.getSource("peaks") as maplibregl.GeoJSONSource)?.setData({
      type: "FeatureCollection",
      features: peakFeatures as unknown as Feature[],
    });
    (map.getSource("trails") as maplibregl.GeoJSONSource)?.setData({
      type: "FeatureCollection",
      features: trailFeatures as unknown as Feature[],
    });
    (map.getSource("landmarks") as maplibregl.GeoJSONSource)?.setData({
      type: "FeatureCollection",
      features: markPeakShown(landmarkFeatures, peakFeatures) as unknown as Feature[],
    });
  }, [features, filter]);

  // Fit camera to an explicit bbox (e.g. a trail's section extent)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fitBounds) return;
    const [w, s, e, n] = fitBounds;
    const apply = () => map.fitBounds([[w, s], [e, n]], { padding: 48, duration: 0 });
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
  }, [fitBounds]);

  // Update explored-cells source (spec 13). Rebuilt only when the array
  // identity changes - the query result is stable between fetches.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sourcesReadyRef.current) return;
    (map.getSource("explored-cells") as maplibregl.GeoJSONSource)?.setData(
      cellsToFeatureCollection(exploredCells) as maplibregl.GeoJSONSourceSpecification["data"]
    );
  }, [exploredCells]);

  // Update heatmap source
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sourcesReadyRef.current) return;
    (map.getSource("heatmap") as maplibregl.GeoJSONSource)?.setData(
      withHeatRecency((heatmap ?? EMPTY_FC) as FeatureCollection)
    );
  }, [heatmap]);

  // Recolour/restyle the trace layer when the equipped map theme or trace
  // style (or heatmap data, whose recency spread the ramp depends on) changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sourcesReadyRef.current) return;
    applyHeatmapTheme(map, mapTheme, traceStyle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapTheme, traceStyle, heatmap]);

  // Recolour collected markers when the equipped pin changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sourcesReadyRef.current) return;
    applyCollectedPinColor(map, collectedPinColor);
  }, [collectedPinColor]);

  // Update region overlay source
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sourcesReadyRef.current) return;
    (map.getSource("region-overlay") as maplibregl.GeoJSONSource)?.setData(
      (regionOverlay ?? EMPTY_FC) as FeatureCollection
    );
  }, [regionOverlay]);

  // Update filter/visibility
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sourcesReadyRef.current) return;
    applyFilter(map, filter, landmarkCategories);
  }, [filter, landmarkCategories]);

  // Highlight selected feature
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sourcesReadyRef.current) return;
    if (map.getLayer("peaks-symbol")) {
      map.setLayoutProperty(
        "peaks-symbol",
        "icon-size",
        peakIconSize(selectedId, { unclustered: !clusterRef.current }),
      );
    }
    if (map.getLayer("landmarks-symbol")) {
      map.setLayoutProperty("landmarks-symbol", "icon-size", landmarkIconSize(selectedId));
    }
  }, [selectedId]);

  return (
    <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
  );
}
